import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useStore } from "vuex";
  import {
    Bed,
    Bath,
    Car,
    Maximize2,
    X,
    Home,
    LayoutTemplate,
  } from "lucide-vue-next";
  import CustomerNavbar from "@/v2/components/CustomerNavbar.vue";
  import CustomerBottomNavigation from "@/v2/components/CustomerBottomNavigation.vue";
  import interactiveMasterplanApi from "@/services/api/interactiveMasterplanApi";
  import projectPageApi, {
    ProjectPageData,
  } from "@/services/api/projectPageApi";
  import { formatPrice } from "@/helpers/transformation/price";
  import {
    Floorplan,
    HouseDesignBathrooms,
  } from "@/types/HouseDesignInterface";

  interface CompatibleLot {
    _id: string;
    title: string;
    status?: string;
    price: number;
    lotPrice: number;
    houseBasePrice: number;
    size?: number;
    hidePrice?: boolean;
    boundingBox?: unknown;
    facade?: unknown;
    houseDesign?: unknown;
  }

  interface HouseDesignWithLots {
    _id: string;
    name: string;
    description?: string;
    basePrice?: number;
    mainImages?: string[];
    floorplans?: Floorplan[];
    bedroom?: number;
    bathroom?: HouseDesignBathrooms;
    garage?: number;
    area?: number;
    compatibleLots: CompatibleLot[];
    lowestPrice: number | null;
  }

  type BottomNavView =
    | "hero"
    | "browse"
    | "detail"
    | "eoi"
    | "eoi-success"
    | "map"
    | "resources";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BrowseHomeDesignsView',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const router = useRouter();
  const store = useStore();

  const houseDesigns = ref<HouseDesignWithLots[]>([]);
  const loading = ref(false);
  const projectPageData = ref<ProjectPageData | null>(null);
  const estateName = ref("");

  // ── Filters ──────────────────────────────────────────────────────────────
  const SIZE_BUCKETS = [
    { label: "Under 120 m²", key: "under-120", min: 0, max: 120 },
    { label: "120–160 m²", key: "120-160", min: 120, max: 160 },
    { label: "160 m²+", key: "over-160", min: 160, max: Infinity },
  ];

  const imageMode = ref<"exterior" | "floorplan">("exterior");

  const filterBedrooms = ref<number | null>(null);
  const filterBathrooms = ref<number | null>(null);
  const filterGarage = ref<number | null>(null);
  const filterSizeBucket = ref<string | null>(null);

  const hasActiveFilters = computed(
    () =>
      filterBedrooms.value !== null ||
      filterBathrooms.value !== null ||
      filterGarage.value !== null ||
      filterSizeBucket.value !== null
  );

  const filteredDesigns = computed(() => {
    return houseDesigns.value.filter((d) => {
      if (
        filterBedrooms.value !== null &&
        (d.bedroom ?? 0) < filterBedrooms.value
      )
        return false;

      const baths =
        typeof d.bathroom === "object" ? d.bathroom?.quantity : d.bathroom;

      if (
        filterBathrooms.value !== null &&
        (baths ?? 0) < filterBathrooms.value
      )
        return false;

      if (filterGarage.value !== null && (d.garage ?? 0) < filterGarage.value)
        return false;

      if (filterSizeBucket.value !== null) {
        const bucket = SIZE_BUCKETS.find(
          (b) => b.key === filterSizeBucket.value
        );

        if (
          bucket &&
          ((d.area ?? 0) < bucket.min || (d.area ?? 0) >= bucket.max)
        )
          return false;
      }

      return true;
    });
  });

  const clearFilters = () => {
    filterBedrooms.value = null;
    filterBathrooms.value = null;
    filterGarage.value = null;
    filterSizeBucket.value = null;
  };

  const estateId = computed(() => route.params.estateId as string);
  const isEmbedded = computed(() => route.query.embed === "true");

  const slugifyEstateTitle = (title?: string) => {
    if (!title) return "";

    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const estateSlug = computed(() => slugifyEstateTitle(estateName.value));

  const fetchHouseDesigns = async () => {
    if (!estateId.value) return;

    try {
      loading.value = true;
      const response = await interactiveMasterplanApi.getHouseDesignsWithLots(
        estateId.value
      );

      houseDesigns.value = response.data || [];
    } catch (error) {
      console.error("Error fetching house designs:", error);
      houseDesigns.value = [];
    } finally {
      loading.value = false;
    }
  };

  const fetchProjectPageData = async () => {
    if (!estateId.value) return;

    try {
      const { data } = await projectPageApi.getByEstateIdPublic(estateId.value);

      projectPageData.value = data;
    } catch (err) {
      console.log("No project page data found:", err);
    }
  };

  const fetchEstateName = () => {
    if (!estateId.value) return;

    const estate = store.state.user.landownerEstates?.find(
      (e: any) => e._id === estateId.value
    );

    if (estate?.title) {
      estateName.value = estate.title;
    }
  };

  const goBack = () => {
    const estate = store.state.user.landownerEstates?.find(
      (e: any) => e._id === estateId.value
    );
    const slug = slugifyEstateTitle(estate?.title || estateName.value);

    if (slug) {
      router.push(`/residential-communities/${slug}`);
    } else {
      router.back();
    }
  };

  onMounted(async () => {
    fetchEstateName();
    await Promise.all([fetchHouseDesigns(), fetchProjectPageData()]);
  });

  const truncateDescription = (description?: string) => {
    if (!description) return "Thoughtfully designed for modern living.";

    const maxLength = 120;

    if (description.length <= maxLength) return description;

    return description.slice(0, maxLength).trim() + "...";
  };

  const formatArea = (area?: number) => {
    if (!area) return "0 m²";

    return `${area.toFixed(1)} m²`;
  };

  const viewHouse = (houseId: string) => {
    router.push({
      name: "HouseInteriorsV2",
      params: { houseId },
      query: {
        estateId: estateId.value,
        ...(isEmbedded.value ? { embed: "true" } : {}),
      },
    });
  };

  const openMap = () => {
    if (!estateId.value) return;

    router.push({
      name: "MasterplanViewV2",
      params: { estateId: estateId.value },
      query: isEmbedded.value ? { embed: "true" } : {},
    });
  };

  const handleBottomNavigate = (view: BottomNavView) => {
    if (view === "browse") {
      router.push({
        name: "BrowseHomeDesignsV2",
        params: { estateId: estateId.value },
        query: isEmbedded.value ? { embed: "true" } : {},
      });
      return;
    }

    if (view === "map") {
      openMap();
      return;
    }

    if (view === "resources" && estateSlug.value) {
      router.push({
        name: "PublicProjectPage",
        params: { slug: estateSlug.value },
      });
    }
  };

const __returned__ = { route, router, store, houseDesigns, loading, projectPageData, estateName, SIZE_BUCKETS, imageMode, filterBedrooms, filterBathrooms, filterGarage, filterSizeBucket, hasActiveFilters, filteredDesigns, clearFilters, estateId, isEmbedded, slugifyEstateTitle, estateSlug, fetchHouseDesigns, fetchProjectPageData, fetchEstateName, goBack, truncateDescription, formatArea, viewHouse, openMap, handleBottomNavigate, computed, onMounted, ref, get useRoute() { return useRoute }, get useRouter() { return useRouter }, get useStore() { return useStore }, get Bed() { return Bed }, get Bath() { return Bath }, get Car() { return Car }, get Maximize2() { return Maximize2 }, get X() { return X }, get Home() { return Home }, get LayoutTemplate() { return LayoutTemplate }, CustomerNavbar, CustomerBottomNavigation, get interactiveMasterplanApi() { return interactiveMasterplanApi }, get projectPageApi() { return projectPageApi }, get ProjectPageData() { return ProjectPageData }, get formatPrice() { return formatPrice }, get Floorplan() { return Floorplan }, get HouseDesignBathrooms() { return HouseDesignBathrooms } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})